/// Per-agent card wrapper for the Configure step. Owns expert
/// identity (gradient header band + ExpertMark + name + [short] +
/// domain + "Change expert" pill); children render inside the card
/// body (Archetype band → preview → Fine-tune → Behavior band).
///
/// Single-agent meetings today render exactly one card. If the
/// single-agent cap lifts, stacking N cards is a `.map(...)` — the
/// card is the unit of per-agent scope.

import type { ReactNode } from "react";
import type { ExpertSummary } from "../../api";
import { ExpertMark } from "../design/ExpertMark";
import { Icon } from "../design/Icon";

const ROSTER_ANCHOR = '[data-step="3"]';

export function AgentConfigCard({
  expert,
  onChangeExpert,
  children,
}: {
  expert: ExpertSummary;
  /** Override the "Change expert" pill's default scroll-to-roster
   *  behavior. Useful for tests + future multi-agent UX where the pill
   *  becomes a "Remove this agent" affordance per card. */
  onChangeExpert?: () => void;
  children: ReactNode;
}) {
  const c = expert.color_token;

  function handleChangeExpert() {
    if (onChangeExpert) {
      onChangeExpert();
      return;
    }
    if (typeof document === "undefined") return;
    const el = document.querySelector(ROSTER_ANCHOR);
    if (el && typeof (el as HTMLElement).scrollIntoView === "function") {
      (el as HTMLElement).scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  return (
    <div
      style={{
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid color-mix(in oklab, var(--${c}) 32%, var(--rule))`,
        background: "var(--card)",
        boxShadow: "var(--sh-1)",
      }}
    >
      {/* Header band — expert identity */}
      <div
        style={{
          background: `linear-gradient(180deg, var(--${c}-tint), color-mix(in oklab, var(--${c}-tint) 60%, var(--card)))`,
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          borderBottom: `1px solid color-mix(in oklab, var(--${c}) 25%, transparent)`,
        }}
      >
        <ExpertMark expert={expert} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span
              className="serif"
              style={{
                fontSize: 22,
                fontWeight: 400,
                lineHeight: 1.1,
                color: `var(--${c}-deep)`,
              }}
            >
              {expert.display_name}
            </span>
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: `var(--${c}-deep)`,
                opacity: 0.75,
              }}
            >
              [{expert.short_name}]
            </span>
          </div>
          <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>
            {expert.persona_summary}
          </div>
        </div>
        <button
          type="button"
          onClick={handleChangeExpert}
          className="mono"
          style={{
            fontSize: 11,
            color: `var(--${c}-deep)`,
            textDecoration: "none",
            padding: "5px 10px",
            borderRadius: 999,
            border: `1px solid color-mix(in oklab, var(--${c}) 30%, var(--rule))`,
            background: "var(--card)",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            letterSpacing: ".04em",
            fontFamily: "inherit",
          }}
        >
          <Icon name="arrowL" size={10} color={`var(--${c}-deep)`} />
          Change expert
        </button>
      </div>

      {/* Body — Archetype / Sample / Fine-tune / Behavior */}
      <div>{children}</div>
    </div>
  );
}
